import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { getAllRepos } from '../../../services/githubService';

const FeaturedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    async function fetchRepos() {
      try {
        setLoading(true);
        const repos = await getAllRepos();
        if (repos && Array.isArray(repos)) {
         console.log('Fetched repos:', repos);
            setProjects(repos.map(repo => ({
              id: repo.id, // Add id for stable key
              title: repo.name,
              description: repo.description || 'No description provided.',
              language: repo.language || 'Other',
              github_url: repo.html_url,
              technologies: [repo.language].filter(Boolean),
            })));
        }
      } catch (err) {
        setError('Failed to load featured projects');
      } finally {
        setLoading(false);
      }
    }
    fetchRepos();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const ProjectCard = ({ project, index }) => (
    <motion.div
      variants={itemVariants}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 cursor-pointer"
      onClick={() => {
        if (project.github_url) {
          window.open(project.github_url, '_blank');
        }
      }}
      role="button"
      tabIndex={0}
      onKeyPress={e => {
        if (e.key === 'Enter' && project.github_url) {
          window.open(project.github_url, '_blank');
        }
      }}
    >
      {/* Project Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={project?.image_url || project?.project_images?.[0]?.image_url || `https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800`}
          alt={project?.project_images?.[0]?.alt_text || `${project?.title} project screenshot`}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800'; }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Featured Badge */}
        {project?.featured && (
          <div className="absolute top-4 left-4">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              <Icon name="Star" size={12} className="mr-1" />
              Featured
            </span>
          </div>
        )}

        {/* Category Badge */}
        <div className="absolute top-4 right-4">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {project?.category?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()) || 'Project'}
          </span>
        </div>

        {/* Action Buttons (shown on hover) */}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex space-x-3">
            <Button
              as="a"
              href={project?.github_url}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              variant="outline"
              className="border-white text-white hover:bg-white hover:text-slate-900"
              iconName="Github"
            >
              Code
            </Button>
          </div>
        </div>
      </div>

      {/* Project Content */}
      <div className="p-6">
        <div className="mb-3">
          <h3 className="text-xl font-bold text-slate-900 mb-2 group-hover:text-blue-600 transition-colors">{project?.title}</h3>
          <p className="text-slate-600 text-sm line-clamp-2 leading-relaxed">{project?.description}</p>
        </div>

        {/* Technologies */}
        {project?.technologies?.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {project?.technologies?.slice(0, 4)?.map((tech, techIndex) => (
                <span key={techIndex} className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded font-medium">{tech}</span>
              ))}
              {project?.technologies?.length > 4 && (
                <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded font-medium">+{project?.technologies?.length - 4} more</span>
              )}
            </div>
          </div>
        )}

        {/* Project Stats */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
          <div className="flex items-center space-x-4 text-sm text-slate-500">
            {project?.view_count > 0 && (
              <div className="flex items-center space-x-1">
                <Icon name="Eye" size={16} />
                <span>{project?.view_count}</span>
              </div>
            )}
            {project?.client_name && (
              <div className="flex items-center space-x-1">
                <Icon name="Building" size={16} />
                <span className="truncate max-w-20">{project?.client_name}</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center text-slate-400">
            <Icon name="ArrowRight" size={16} className="group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Featured Projects</h2>
            <p className="text-xl text-slate-600">Showcasing my latest work</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)]?.map((_, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-lg animate-pulse">
                <div className="h-48 bg-slate-200"></div>
                <div className="p-6">
                  <div className="h-6 bg-slate-200 rounded mb-2"></div>
                  <div className="h-4 bg-slate-100 rounded mb-4"></div>
                  <div className="flex gap-2 mb-4">
                    <div className="h-6 w-16 bg-slate-100 rounded"></div>
                    <div className="h-6 w-20 bg-slate-100 rounded"></div>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="h-4 w-24 bg-slate-100 rounded"></div>
                    <div className="h-4 w-4 bg-slate-100 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Unable to Load Projects</h3>
            <p className="text-slate-600 mb-6">{error}</p>
            <Button onClick={() => window?.location?.reload()} iconName="RefreshCw" iconPosition="left">Try Again</Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">Featured Projects</h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">A showcase of my recent work, demonstrating expertise in modern web technologies and creative problem-solving across various industries.</p>
        </motion.div>

        {/* Projects Grid */}
        {projects?.length > 0 ? (
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {(showAll ? projects : projects.slice(0, 6)).map((project, index) => (
              <ProjectCard key={project?.id ?? project?.github_url ?? project?.title ?? index} project={project} index={index} />
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Briefcase" size={48} className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-600 mb-2">No Featured Projects</h3>
            <p className="text-slate-500">Check back soon for new project showcases.</p>
          </div>
        )}

        {/* View All Projects Button */}
        {projects?.length > 6 && !showAll && (
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }} className="text-center">
            <Button
              as="a"
              href="/portfolio#projects"
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
              iconName="ArrowRight"
              iconPosition="right"
            >
              View All Projects
            </Button>
          </motion.div>
        )}
        {showAll && projects?.length > 6 && (
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }} className="text-center">
            <Button onClick={() => { console.log('Show less pressed, projects:', projects); setShowAll(false); }} size="lg" className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white" iconName="ArrowLeft" iconPosition="left">Show Less</Button>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default FeaturedProjects;
